import React from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Box
} from '@mui/material';
import { SelectLocation } from '../../../common';

export default function ReceiptHistoryTab({
  suppliers,
  receiptFilters,
  onReceiptFilterChange,
  clearReceiptFilters,
  isReceiptsLoading,
  receipts,
  receiptCount,
  receiptPagination,
  onReceiptPageChange,
  onReceiptRowsPerPageChange,
  isReceiptsFetching,
  openReceiptDetail,
  receiptDetailOpen,
  setReceiptDetailOpen,
  selectedReceipt
}) {
  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Recent Receipts</Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Invoice #"
              value={receiptFilters.invoiceNumber}
              onChange={(e) => onReceiptFilterChange('invoiceNumber', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Supplier"
              value={receiptFilters.supplierId}
              onChange={(e) => onReceiptFilterChange('supplierId', e.target.value)}
            >
              <MenuItem value="">All Suppliers</MenuItem>
              {suppliers.map(supplier => (
                <MenuItem key={supplier.id} value={supplier.id}>{supplier.supplierName}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <SelectLocation
              fullWidth
              location={receiptFilters.locationId}
              setLocation={(loc) => onReceiptFilterChange('locationId', loc?.id || '')}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button variant="outlined" onClick={clearReceiptFilters} sx={{ height: 40 }}>
              Clear Filters
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="From Date"
              value={receiptFilters.fromDate}
              onChange={(e) => onReceiptFilterChange('fromDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="To Date"
              value={receiptFilters.toDate}
              onChange={(e) => onReceiptFilterChange('toDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {isReceiptsLoading ? (
          <Chip label="Loading receipts..." />
        ) : !receipts.length ? (
          <Chip label="No receipts found" />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Receipt #</TableCell>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Received Date</TableCell>
                  <TableCell>Consignment</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Lines</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receipts.map(receipt => (
                  <TableRow
                    key={receipt.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openReceiptDetail(receipt)}
                  >
                    <TableCell>{receipt.id}</TableCell>
                    <TableCell>{receipt.supplierInvoiceNumber}</TableCell>
                    <TableCell>{receipt.supplier?.supplierName || '-'}</TableCell>
                    <TableCell>{receipt.location?.name || '-'}</TableCell>
                    <TableCell>{receipt.receivedDate || '-'}</TableCell>
                    <TableCell>{receipt.consignmentReference || receipt.consignmentType || '-'}</TableCell>
                    <TableCell>
                      <Chip label={receipt.status || 'posted'} size="small" color={receipt.status === 'void' ? 'error' : 'success'} />
                    </TableCell>
                    <TableCell align="right">{receipt.lines?.length || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={receiptCount}
              page={receiptPagination.page}
              onPageChange={onReceiptPageChange}
              rowsPerPage={receiptPagination.rowsPerPage}
              onRowsPerPageChange={onReceiptRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}

        {isReceiptsFetching && !isReceiptsLoading && (
          <Typography variant="caption" color="text.secondary">
            Refreshing receipt list...
          </Typography>
        )}
      </Paper>

      <Dialog open={receiptDetailOpen} onClose={() => setReceiptDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Receipt Detail</DialogTitle>
        <DialogContent>
          {selectedReceipt && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Receipt #:</strong> {selectedReceipt.id}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Invoice #:</strong> {selectedReceipt.supplierInvoiceNumber}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Status:</strong> {selectedReceipt.status || 'posted'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Supplier:</strong> {selectedReceipt.supplier?.supplierName || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Location:</strong> {selectedReceipt.location?.name || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Received Date:</strong> {selectedReceipt.receivedDate || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2"><strong>Consignment Ref:</strong> {selectedReceipt.consignmentReference || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2"><strong>Consignment Type:</strong> {selectedReceipt.consignmentType || 'none'}</Typography>
                </Grid>
              </Grid>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Inventory Item</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell align="right">Accepted</TableCell>
                    <TableCell align="right">Damaged</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Landing Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedReceipt.lines || []).map((line) => {
                    const lineLabel =
                      line.modelNumberAtReceipt ||
                      line.inventoryItem?.modelNumber ||
                      line.purchaseOrderLine?.modelNumber ||
                      `Inventory #${line.inventoryItemId}`;

                    return (
                      <TableRow key={line.id}>
                        <TableCell>
                          {line.brandNameAtReceipt ? `${line.brandNameAtReceipt} ` : ''}
                          {lineLabel}
                        </TableCell>
                        <TableCell align="right">{line.receivedQty}</TableCell>
                        <TableCell align="right">{line.acceptedQty}</TableCell>
                        <TableCell align="right">{line.damagedQty}</TableCell>
                        <TableCell align="right">{line.unitCostAtReceipt ?? '-'}</TableCell>
                        <TableCell align="right">{line.landingCostAtReceipt ?? '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}